import { CloseOutlined, DownloadOutlined, FilterAltOutlined, Search, StarBorder, ViewWeekOutlined } from '@mui/icons-material'
import { Box, Button, IconButton, InputAdornment, Stack, Theme, Tooltip, Typography } from '@mui/material'
import {
  GridColDef,
  GridFilterModel,
  GridSlotsComponentsProps,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium'
import uniq from 'lodash/uniq'
import { MouseEvent, PropsWithChildren, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { requestsTableName } from '@omnea-app/fe-requests/requestsTableName'
import { filterItemIsValid } from '@omnea-app/packages/universal/saved-filters/validateFilterModel'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { AiFilterCreator } from '../../ai-filter-creator/AiFilterCreator'
import { useGridShowsSkeleton } from '../../data-grid/hooks/useGridShowsSkeleton'
import { RowGrouping } from '../../row-grouping/RowGrouping'
import { ToolbarSurface } from '../../ToolbarSurface'
import { useSavedFilterContext } from './SavedFilterContext'

export type ExportOptions = {
  fileName: string
  fields?: string[]
}

declare module '@mui/x-data-grid-premium' {
  interface ToolbarPropsOverrides {
    tableName: string
    exportOptions: ExportOptions
    displayRowGroupingButton?: boolean
    hideSearchBar?: boolean
    disableExportInToolbar?: boolean
    exportLoading?: boolean
    hideControlsRow?: boolean
    header?: ReactNode
    onExportClick?: (event: MouseEvent<HTMLElement>) => void
  }
}

export const ControlledTableToolbar = ({
  tableName,
  exportOptions,
  displayRowGroupingButton,
  hideSearchBar,
  disableExportInToolbar,
  exportLoading,
  header,
  hideControlsRow,
  onExportClick,
}: NonNullable<GridSlotsComponentsProps['toolbar']>) => {
  const { t } = useTranslation('common')
  const { savedFilter, onUnselectSavedFilter } = useSavedFilterContext()
  const apiRef = useGridApiContext()
  const { GEN_AI } = useFeatureFlags()
  const showsSkeleton = useGridShowsSkeleton()

  const areColumnsGroupable = useMemo(() => {
    const columns = apiRef.current?.getAllColumns()
    return columns?.some((column) => column.groupable)
  }, [apiRef])

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)

  const filteredColumnsNames = getFilteredColumnNames(filterModel, apiRef.current?.getColumn)

  if (hideControlsRow) return isDefined(header) ? <ToolbarSurface>{header}</ToolbarSurface> : null

  return (
    <ToolbarSurface>
      {header && header}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={(theme) => ({ padding: theme.spacing(2.5, 2.5, 2.5, 3) })}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            gap: 1.5,
          }}
        >
          {savedFilter && <SavedFilterBadge>{savedFilter.name}</SavedFilterBadge>}

          <GridToolbarFilterButton
            slotProps={{
              button: {
                className: filteredColumnsNames ? 'active' : undefined,
                color: 'secondary',
                disabled: showsSkeleton,
                startIcon: (
                  <Stack direction="row" gap={0.5}>
                    <FilterAltOutlined sx={{ width: 16, m: 0, p: 0 }} />
                    <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="body-medium">
                      {filteredColumnsNames
                        ? t('table.table-toolbar.active-filter-button-label', { columnList: filteredColumnsNames.join(', ') })
                        : t('table.table-toolbar.empty-filter-button-label')}
                    </Typography>
                    {filteredColumnsNames && filteredColumnsNames.length > 0 && (
                      <IconButton
                        size="small"
                        sx={{
                          padding: 0,
                          // Right margin offsets the startIcon right margin
                          marginRight: -2,
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          apiRef.current?.setFilterModel({ items: [] })
                          if (savedFilter) {
                            onUnselectSavedFilter()
                          }
                        }}
                      >
                        <CloseOutlined sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </Stack>
                ),
                variant: 'text',
              },
            }}
          />
          {GEN_AI &&
            tableName === requestsTableName && (
              // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
              <AiFilterCreator tableName={tableName!} apiRef={apiRef} disabled={showsSkeleton} />
            )}
          {displayRowGroupingButton && areColumnsGroupable && <RowGrouping disabled={showsSkeleton} />}
          <Tooltip title={t('table.table-toolbar.tooltip-columns')}>
            <GridToolbarColumnsButton
              slotProps={{
                button: {
                  color: 'secondary',
                  variant: 'text',
                  disabled: showsSkeleton,
                  startIcon: (
                    <Stack direction="row" gap={0.5}>
                      <ViewWeekOutlined sx={{ width: 16 }} />
                      <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="body-medium">
                        {t('table.table-toolbar.columns-button-label')}
                      </Typography>
                    </Stack>
                  ),
                },
              }}
            />
          </Tooltip>
          {!disableExportInToolbar && (
            <Tooltip title={exportLoading ? t('table.table-toolbar.tooltip-export-loading') : ''}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {onExportClick != null ? (
                  <Button
                    color="secondary"
                    variant="text"
                    disabled={exportLoading || showsSkeleton}
                    onClick={onExportClick}
                    startIcon={
                      <Stack direction="row" gap={0.5} sx={{ color: 'inherit', justifyContent: 'center', alignItems: 'center' }}>
                        <DownloadOutlined sx={{ width: 16 }} />
                        <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="body-medium">
                          {t('table.table-toolbar.export-button-label')}
                        </Typography>
                      </Stack>
                    }
                  />
                ) : (
                  <GridToolbarExport
                    csvOptions={exportOptions}
                    excelOptions={exportOptions}
                    slotProps={{
                      button: {
                        color: 'secondary',
                        variant: 'text',
                        disabled: exportLoading || showsSkeleton,
                        startIcon: (
                          <Stack
                            direction="row"
                            gap={0.5}
                            sx={{ color: 'inherit', justifyContent: 'center', alignItems: 'center' }}
                          >
                            <DownloadOutlined sx={{ width: 16 }} />
                            <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="body-medium">
                              {t('table.table-toolbar.export-button-label')}
                            </Typography>
                          </Stack>
                        ),
                      },
                    }}
                  />
                )}
              </span>
            </Tooltip>
          )}
        </Stack>
        {!hideSearchBar && (
          <GridToolbarQuickFilter
            placeholder={t('search')}
            debounceMs={500}
            sx={{ marginBottom: 0 }}
            InputProps={{
              startAdornment: SearchStartAdornment,
              endAdornment: null,
              size: 'small',
            }}
          />
        )}
      </Stack>
    </ToolbarSurface>
  )
}

const SearchStartAdornment = (
  <InputAdornment position="start">
    <Search sx={{ width: 18 }} />
  </InputAdornment>
)

const getFilteredColumnNames = (
  filterModel: GridFilterModel | undefined,
  getColumn: undefined | ((field: string) => GridColDef)
): string[] | null => {
  if (!filterModel || !getColumn) return null
  const filterItems = filterModel?.items

  if (!filterItems) return null

  const allFilteredColumnNames = filterItems.filter(filterItemIsValid).map(({ field }) => field)

  if (allFilteredColumnNames.length < 1) return null

  const uniqueNames = uniq(allFilteredColumnNames)
    .map(getColumn)
    .map((columnDef) => columnDef?.headerName)
    .filter(isDefined)

  if (uniqueNames.length < 1) return null

  return uniqueNames
}

const SavedFilterBadge: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',

        padding: '3px 6px',
        color: theme.palette.fg.inverted,
        backgroundColor: theme.palette.bg.inverted,
      })}
    >
      <StarBorder sx={{ width: 16, height: 16 }} />
      <Typography variant="small-medium">{children}</Typography>
    </Box>
  )
}
